import { motion } from 'framer-motion';
import { Activity, BrainCircuit, Camera, FileCheck2, MonitorCheck, Smartphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';

const features = [
  ['Dual-camera monitoring', 'Pair a phone camera with the laptop feed for richer review evidence.', Smartphone],
  ['Browser event tracking', 'Fullscreen, tab, clipboard, and zoom-attempt signals are recorded internally.', MonitorCheck],
  ['AI-assisted review', 'Throttled visual and audio analysis supports reviewers without blocking the exam.', BrainCircuit],
  ['Explainable reports', 'Candidate sessions export with timelines, warnings, evidence, and recommendations.', FileCheck2]
];

export function LandingPage() {
  return (
    <section className="relative isolate overflow-hidden">
      <div className="absolute right-[8%] top-24 h-72 w-72 fluid-orb opacity-80 blur-[1px]" aria-hidden />
      <div className="absolute left-[6%] top-[58%] h-40 w-40 fluid-orb opacity-40" aria-hidden />
      <div className="mx-auto grid min-h-[calc(100vh-65px)] max-w-7xl gap-10 px-4 py-14 lg:grid-cols-[1.02fr_0.98fr] lg:items-center">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.08] px-4 py-2 text-sm font-semibold text-neon-blue">
            <Activity className="h-4 w-4" /> Premium examination simulation
          </div>
          <h1 className="mt-6 max-w-4xl text-5xl font-black leading-tight tracking-normal text-white md:text-7xl">
            ExamSphere AI
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-8 text-slate-300">
            AI-powered mock examinations and proctoring with dual-camera pairing, reviewer decision support, and explainable reports.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/login"><Button>Start Free Mock Exam</Button></Link>
            <a href="#features"><Button variant="secondary">Explore Platform</Button></a>
          </div>
        </motion.div>

        <motion.div id="features"
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.45, delay: 0.1 }}
          className="glass rounded-2xl p-5 shadow-neon"
        >
          <div className="grid aspect-[4/3] gap-4 rounded-xl border border-white/10 bg-black/35 p-4 md:grid-cols-[1.15fr_0.85fr]">
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-950 via-indigo-950 to-black">
              <div className="absolute inset-8 rounded-[2rem] border border-neon-blue/30 bg-neon-blue/10 shadow-[0_0_80px_rgba(34,211,238,0.22)]" />
              <Camera className="absolute left-1/2 top-1/2 h-20 w-20 -translate-x-1/2 -translate-y-1/2 text-neon-blue" />
              <div className="absolute bottom-4 left-4 right-4 rounded-full border border-white/10 bg-black/50 px-4 py-2 text-sm text-slate-200 backdrop-blur">
                Laptop camera connected
              </div>
            </div>
            <div className="grid gap-3">
              {features.map(([title, text, Icon]) => (
                <div key={title as string} className="rounded-xl border border-white/10 bg-white/[0.06] p-4">
                  <Icon className="mb-3 h-5 w-5 text-neon-magenta" />
                  <p className="font-semibold text-white">{title as string}</p>
                  <p className="mt-1 text-xs leading-5 text-slate-400">{text as string}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
      <div className="mx-auto max-w-7xl space-y-16 px-4 pb-20">
        <section id="how-it-works" className="scroll-mt-24"><h2 className="text-3xl font-black">How It Works</h2><p className="mt-3 max-w-3xl text-slate-300">Sign in with a scheduled demo assessment, verify devices, pair an optional phone camera, and complete the examination in a focused fullscreen workspace.</p></section>
        <section id="ai-proctoring" className="scroll-mt-24"><h2 className="text-3xl font-black">AI Proctoring</h2><p className="mt-3 max-w-3xl text-slate-300">Throttled AI-assisted signals support human review. They are probabilistic observations, not automatic accusations or final decisions.</p></section>
        <section id="dashboards" className="scroll-mt-24"><h2 className="text-3xl font-black">Dashboards</h2><p className="mt-3 max-w-3xl text-slate-300">Role-protected reviewer and administrator workspaces organize sessions, incidents, evidence, and review decisions.</p></section>
        <section id="reports" className="scroll-mt-24"><h2 className="text-3xl font-black">Reports</h2><p className="mt-3 max-w-3xl text-slate-300">Explainable session reports combine completion data, browser events, camera history, and reviewer recommendations.</p></section>
        <section id="documentation" className="scroll-mt-24"><h2 className="text-3xl font-black">Documentation</h2><p className="mt-3 max-w-3xl text-slate-300">This demonstration uses browser-level monitoring and does not claim operating-system lockdown. Camera access requires browser permission and a secure context on mobile devices.</p></section>
      </div>
    </section>
  );
}
